/* eslint-disable @typescript-eslint/camelcase */

import React from "react";
import classNames from "classnames";
import environment from "web/script/modules/environment";
import MultiplyOnHoverButton from "web/react/components/buttons/multiply-on-hover-button/multiply-on-hover-button";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import LeadLink from "web/react/components/lead-link/lead-link";
import HintBubble from "web/react/components/hint-bubble/hint-bubble";
import Accordion from "web/react/components/accordion/accordion";
import { openShippingAndReturnsOverlay } from "web/redux/ducks/shipping-and-returns-overlay";
import analytics from "web/script/analytics/analytics";
import KlarnaPlacement from "web/react/components/klarna-placement/klarna-placement";
import SecondaryButton from "web/react/components/buttons/secondary-button/secondary-button";
import store from "web/redux/store";
// Conditionally applying a class triggers a fake 'unused' linter error
// eslint-disable-next-line css-modules/no-unused-class
import styles from "./buy-option.css";

export interface BuyOptionProps {
    withSearch: string;
    initialExpanded?: boolean;
    onAddToBag: (any) => void;
    oneClickCheckout: (any) => void;
    date_updated: string;
    free_shipping: {
        has_promo_code: boolean;
        is_lyst_shipping: boolean;
        message: string;
        url: string;
    };
    has_free_shipping: boolean;
    human_size: string;
    icon_affiliate_hybrid_enabled: boolean;
    is_final_sale: boolean;
    is_on_sale: boolean;
    is_saved_for_later: boolean;
    last_checked_at: string;
    lead_label: string;
    lead_url: string;
    link_id: string;
    price: string;
    price_with_currency_symbol: string;
    price_with_shipping: string;
    promotions: any[];
    raw_size: string;
    retailer_name: string;
    retailer_slug: string;
    retailer_tc_link: string;
    retailer_tc_link_text: string;
    sale_discount: number;
    sale_price_with_currency_symbol: string;
    shipping_cost_label: string;
    terms_and_conditions_link: string;
}

function BuyOption({
    initialExpanded,
    onAddToBag,
    oneClickCheckout,
    icon_affiliate_hybrid_enabled,
    lead_url,
    link_id,
    retailer_name,
    retailer_slug,
    is_on_sale,
    sale_price_with_currency_symbol,
    price_with_currency_symbol,
    price_with_shipping,
    shipping_cost_label,
    free_shipping,
    promotions,
    terms_and_conditions_link,
    retailer_tc_link,
    raw_size,
    price,
    sale_discount,
    is_final_sale,
    has_free_shipping,
    human_size,
}: BuyOptionProps): React.ReactElement {
    const [buyDisabled, setBuyDisabled] = React.useState<boolean>(false);
    const [accordionIsOpen, setAccordionIsOpen] = React.useState<boolean>(false);
    // Klarna messages should only appear on Icon only buy options and when the accordion is open
    const shouldDisplayKlarnaOnsiteMessage =
        accordionIsOpen &&
        !icon_affiliate_hybrid_enabled &&
        environment.getFeature("checkout_klarna_messaging");
    const language = environment.get("language");
    const shouldDisplayRetailerRawSize = environment.getFeature("checkp_raw_sizes");

    const buyNowVariant = environment.getFeature("checkout_buy_now");

    function onClickShippingInfo(): void {
        store.dispatch(openShippingAndReturnsOverlay(link_id));
    }

    function onAccordionOpened({ accordionName, nonInteraction }): void {
        setAccordionIsOpen(true);
        analytics.event("buy_area", "open_option", accordionName, nonInteraction);
    }

    function onAccordionClosed({ accordionName, nonInteraction }): void {
        setAccordionIsOpen(false);
        analytics.event("buy_area", "close_option", accordionName, nonInteraction);
    }

    async function onClickAddToBag(): Promise<void> {
        setBuyDisabled(true);

        const options = {
            link_id,
            raw_size,
            price,
            sale_discount,
            retailer_slug,
            is_final_sale,
            has_free_shipping,
        };
        await onAddToBag(options);

        setBuyDisabled(false);
    }

    async function onClickBuyNow(): Promise<any> {
        setBuyDisabled(true);

        const options = {
            link_id,
            raw_size,
            price,
            sale_discount,
            retailer_slug,
            is_final_sale,
            has_free_shipping,
        };
        oneClickCheckout(options);

        setBuyDisabled(false);
    }

    return (
        <Accordion
            initialExpanded={initialExpanded}
            name={retailer_slug}
            onAccordionOpened={onAccordionOpened}
            onAccordionClosed={onAccordionClosed}
        >
            <Accordion.Title activeClassName={styles.titleActive}>
                <h4 className={styles.title}>{retailer_name}</h4>
                <span className={styles.priceContainer}>
                    {is_on_sale && <del className={styles.price}>{price_with_currency_symbol}</del>}
                    <span
                        className={classNames(styles.price, {
                            [styles.salePrice]: is_on_sale,
                        })}
                    >
                        {sale_price_with_currency_symbol}
                    </span>
                </span>
            </Accordion.Title>
            <Accordion.Content>
                <div className={styles.content}>
                    {shouldDisplayRetailerRawSize && (
                        <div className={styles.size} data-test-id="buy-option-size">
                            <span>
                                {human_size ? `${human_size} / ${raw_size}` : `${raw_size}`}
                            </span>
                        </div>
                    )}
                    <div className={styles.shipping}>
                        <div
                            className={styles.shippingAndReturnsLink}
                            onClick={onClickShippingInfo}
                        >
                            {"View shipping & returns info"}
                        </div>
                        {shipping_cost_label && (
                            <div className={styles.shippingCost}>{shipping_cost_label}</div>
                        )}
                    </div>
                    {(free_shipping || promotions) && (
                        <div className={styles.promos}>
                            {promotions.map((promotion, index) => (
                                <div key={index} className={styles.promo}>
                                    <SVGIcon name="tag" className={styles.promoIcon} />
                                    {promotion.url ? (
                                        <a
                                            href={promotion.url}
                                            className={styles.promoLink}
                                            dangerouslySetInnerHTML={{
                                                __html: promotion.message,
                                            }}
                                        />
                                    ) : (
                                        <p
                                            className={styles.promoText}
                                            dangerouslySetInnerHTML={{
                                                __html: promotion.message,
                                            }}
                                        />
                                    )}
                                    {terms_and_conditions_link && retailer_tc_link && (
                                        <HintBubble
                                            tooltipText={
                                                <div className={styles.tooltipContent}>
                                                    <a href={terms_and_conditions_link}>
                                                        {"Lyst promotion terms apply"}
                                                    </a>
                                                    <a
                                                        className={styles.tooltipRetailerLink}
                                                        href={retailer_tc_link}
                                                    >{`${retailer_name} promotion terms apply`}</a>
                                                </div>
                                            }
                                        />
                                    )}
                                </div>
                            ))}

                            {free_shipping && (
                                <div className={styles.promo}>
                                    <SVGIcon name="truck" />
                                    {free_shipping.url ? (
                                        <a
                                            href={free_shipping.url}
                                            className={styles.promoLink}
                                            dangerouslySetInnerHTML={{
                                                __html: free_shipping.message,
                                            }}
                                        />
                                    ) : (
                                        <p
                                            className={styles.promoText}
                                            dangerouslySetInnerHTML={{
                                                __html: free_shipping.message,
                                            }}
                                        />
                                    )}
                                    {free_shipping.has_promo_code && (
                                        <HintBubble
                                            tooltipText={
                                                <div className={styles.tooltipContent}>
                                                    <a href={terms_and_conditions_link}>
                                                        {"Lyst promotion terms apply"}
                                                    </a>
                                                    {!free_shipping.is_lyst_shipping && (
                                                        <a
                                                            className={styles.tooltipRetailerLink}
                                                            href={retailer_tc_link}
                                                        >{`${retailer_name} promotion terms apply`}</a>
                                                    )}
                                                </div>
                                            }
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {(buyNowVariant == "variant_1" && (
                        <div>
                            <MultiplyOnHoverButton
                                className={styles.buyNowButton}
                                onClick={onClickBuyNow}
                                disabled={buyDisabled}
                                openInNewTab={false}
                                fullHeight
                                fullWidth
                            >
                                <span>{"Buy now"}</span>
                            </MultiplyOnHoverButton>
                            {icon_affiliate_hybrid_enabled && (
                                <LeadLink
                                    className={styles.buyOnStoreButton}
                                    href={lead_url}
                                    productId={link_id}
                                    reason="buy-on-store-button"
                                    openInNewTab
                                >
                                    <SVGIcon className={styles.hyperlinkIcon} name="hyperlink" />
                                    <span>{` buy on ${retailer_name}`}</span>
                                </LeadLink>
                            )}
                        </div>
                    )) ||
                        (buyNowVariant == "variant_2" && (
                            <div>
                                <MultiplyOnHoverButton
                                    className={styles.buyNowButton}
                                    onClick={onClickBuyNow}
                                    disabled={buyDisabled}
                                    openInNewTab={false}
                                    fullHeight
                                    fullWidth
                                >
                                    <span>{"Buy now"}</span>
                                </MultiplyOnHoverButton>
                                {(icon_affiliate_hybrid_enabled && (
                                    <LeadLink
                                        className={styles.buyOnStoreButton}
                                        href={lead_url}
                                        productId={link_id}
                                        reason="buy-on-store-button"
                                        openInNewTab
                                    >
                                        <SVGIcon
                                            className={styles.hyperlinkIcon}
                                            name="hyperlink"
                                        />
                                        <span>{` buy on ${retailer_name}`}</span>
                                    </LeadLink>
                                )) || (
                                    <SecondaryButton
                                        className={styles.addToBagSecondaryButton}
                                        onClick={onClickAddToBag}
                                        disabled={buyDisabled}
                                        openInNewTab={false}
                                    >
                                        <span>{"Add to bag"}</span>
                                    </SecondaryButton>
                                )}
                            </div>
                        )) || (
                            <div>
                                <MultiplyOnHoverButton
                                    className={classNames(
                                        styles.addToBagButton,
                                        "buybuybuy-option__buy-button" // TODO: Refactor classname when migrating sticky buy button
                                    )}
                                    onClick={onClickAddToBag}
                                    disabled={buyDisabled}
                                    openInNewTab={false}
                                    fullHeight
                                    fullWidth
                                >
                                    <SVGIcon name="plus-bag" className={styles.plusBagIcon} />
                                    <span>{"Add to bag"}</span>
                                </MultiplyOnHoverButton>
                                {icon_affiliate_hybrid_enabled && (
                                    <LeadLink
                                        className={styles.buyOnStoreButton}
                                        href={lead_url}
                                        productId={link_id}
                                        reason="buy-on-store-button"
                                        openInNewTab
                                    >
                                        <SVGIcon
                                            className={styles.hyperlinkIcon}
                                            name="hyperlink"
                                        />
                                        <span>{` buy on ${retailer_name}`}</span>
                                    </LeadLink>
                                )}
                            </div>
                        )}
                    {shouldDisplayKlarnaOnsiteMessage && (
                        <KlarnaPlacement
                            price={price_with_shipping}
                            language={language}
                            category="buy_area"
                        />
                    )}
                </div>
            </Accordion.Content>
        </Accordion>
    );
}

export default BuyOption;
